import {
  postV1CompaniesCompanyIdTreadAgentConversations,
  postV1CompaniesCompanyIdTreadAgentConversationsConversationIdMessages,
  putV1CompaniesCompanyIdTreadAgentConversationsConversationIdToolCallsBulkConfirm,
  putV1CompaniesCompanyIdTreadAgentConversationsConversationIdToolCallsIdCancel,
  putV1CompaniesCompanyIdTreadAgentConversationsConversationIdToolCallsIdConfirm,
  putV1CompaniesCompanyIdTreadAgentConversationsIdStop,
} from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback, useRef } from 'react';

import { TreadAgentConversation, TreadAgentMessage } from '~hooks/useTreadAgent/models';
import { queryClient } from '~queries/queryClient';
import { TREAD_AGENT_QUERY_KEY } from '~queries/treadAgentQueryKeys';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { BulkConfirmDecision } from '~tread-agent/types';

/**
 * The imperative Tread Agent API surface — create / send / confirm / cancel / stop. Per our
 * standards all API requests live in a hook; TreadAgentStore owns only the live turn, the
 * ActionCable subscription, and the transcript cache. These functions call the generated
 * client and drive the store for the state each request implies.
 */
export default function useTreadAgent() {
  const { treadAgentStore } = useStores();
  // Synchronous in-flight guard: reject a rapid second submit before the composer re-renders
  // disabled, so a double-click can't fire a duplicate request.
  const isSendingMessage = useRef(false);

  const createConversation = useCallback(
    async (companyId: string) => {
      try {
        const response = await postV1CompaniesCompanyIdTreadAgentConversations({
          path: { 'company-id': companyId },
        });
        const conversation = TreadAgentConversation.parse(response.data.data);

        treadAgentStore.openConversation(companyId, conversation.id);
        queryClient.invalidateQueries({ queryKey: [TREAD_AGENT_QUERY_KEY] });

        return conversation;
      } catch (error) {
        // handleRequestError reports (toast + Sentry) then throws; swallow that throw so this
        // resolves to undefined and sendMessage's `if (!conversation) return` bails cleanly,
        // rather than propagating into a second, misleading "failed to send" toast.
        try {
          connection.handleRequestError(
            error,
            t('error_messages.tread_agent.failed_to_create_chat'),
          );
        } catch {
          // Already reported above.
        }
      }
    },
    [treadAgentStore],
  );

  /**
   * Sends a message in the active conversation, creating one first if needed (the floating
   * panel's quick-ask path). Returns the conversation id so the caller can navigate to it.
   * The streamed reply arrives over the channel.
   */
  const sendMessage = useCallback(
    async (companyId: string, content: string, fileSignedIds: string[] = []) => {
      // Reject a rapid second submit that races in before the composer re-renders disabled.
      if (isSendingMessage.current) {
        return;
      }
      isSendingMessage.current = true;

      // Whether a conversation was already open before this send. If we create one here and
      // the send then fails, we roll it back so the store isn't left subscribed to an empty
      // conversation the user never got into.
      const hadActiveConversation = treadAgentStore.activeConversationId !== null;

      try {
        let conversationId = treadAgentStore.activeConversationId;

        if (!conversationId) {
          const conversation = await createConversation(companyId);

          if (!conversation) {
            return;
          }

          conversationId = conversation.id;
        }

        const response =
          await postV1CompaniesCompanyIdTreadAgentConversationsConversationIdMessages({
            path: { 'company-id': companyId, 'conversation-id': conversationId },
            body: {
              content,
              ...(fileSignedIds.length > 0 ? { file_signed_ids: fileSignedIds } : {}),
            },
          });

        // The user may have closed the panel, started a fresh chat, or switched
        // company/conversation while the POST was in flight. If the store no longer points at
        // the conversation we sent to, drop the result rather than repopulating a closed or
        // rescoped store (the message is still persisted server-side).
        if (treadAgentStore.activeConversationId !== conversationId) {
          return conversationId;
        }

        // Cancel the new conversation's in-flight initial transcript fetch so it can't resolve
        // empty and clobber the message we fold in next (standard RQ optimistic guard; a no-op
        // for an existing conversation whose transcript already settled).
        await queryClient.cancelQueries({
          queryKey: [TREAD_AGENT_QUERY_KEY, 'messages', companyId, conversationId],
        });
        treadAgentStore.writeMessageToCache(
          companyId,
          conversationId,
          TreadAgentMessage.parse(response.data.data),
        );
        treadAgentStore.beginStreamingTurn(conversationId);

        return conversationId;
      } catch (error) {
        if (!hadActiveConversation) {
          treadAgentStore.closeConversation();
        }

        // A 422 is the backend rejecting the message's attachments (unsupported type,
        // oversized, or too many). Re-throw it so the composer can surface the reason inline
        // and keep the user's draft + files, rather than firing a generic toast.
        if (isAxiosError(error) && error.response?.status === 422) {
          throw error;
        }

        connection.handleRequestError(
          error,
          t('error_messages.tread_agent.failed_to_send_message'),
        );
      } finally {
        isSendingMessage.current = false;
      }
    },
    [createConversation, treadAgentStore],
  );

  const confirmToolCall = useCallback(
    async (companyId: string, conversationId: string, toolCallId: string) => {
      try {
        await putV1CompaniesCompanyIdTreadAgentConversationsConversationIdToolCallsIdConfirm(
          {
            path: {
              'company-id': companyId,
              'conversation-id': conversationId,
              id: toolCallId,
            },
          },
        );
        // The authoritative resolution arrives as a tool_call_resolved event; the follow-up
        // summary turn then streams in. Nothing to apply optimistically here.
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.tread_agent.failed_to_confirm_action'),
        );
      }
    },
    [],
  );

  const cancelToolCall = useCallback(
    async (companyId: string, conversationId: string, toolCallId: string) => {
      try {
        await putV1CompaniesCompanyIdTreadAgentConversationsConversationIdToolCallsIdCancel(
          {
            path: {
              'company-id': companyId,
              'conversation-id': conversationId,
              id: toolCallId,
            },
          },
        );
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.tread_agent.failed_to_cancel_action'),
        );
      }
    },
    [],
  );

  const bulkConfirmToolCalls = useCallback(
    async (companyId: string, conversationId: string, decision: BulkConfirmDecision) => {
      try {
        await putV1CompaniesCompanyIdTreadAgentConversationsConversationIdToolCallsBulkConfirm(
          {
            path: { 'company-id': companyId, 'conversation-id': conversationId },
            body: {
              canceled_ids: decision.canceledIds,
              confirmed_ids: decision.confirmedIds,
            },
          },
        );
      } catch (error) {
        // A 409 or 422 means a card on screen no longer matches the server's group, so reload it.
        if (isAxiosError(error) && [409, 422].includes(error.response?.status ?? 0)) {
          queryClient.invalidateQueries({ queryKey: [TREAD_AGENT_QUERY_KEY] });
        }

        connection.handleRequestError(
          error,
          t('error_messages.tread_agent.failed_to_confirm_action'),
        );
      }
    },
    [],
  );

  const stopTurn = useCallback(
    async (companyId: string, conversationId: string) => {
      // The composer's control disables off this flag, but only once the render commits.
      if (treadAgentStore.isStopRequested) {
        return;
      }

      treadAgentStore.markStopRequested();

      try {
        await putV1CompaniesCompanyIdTreadAgentConversationsIdStop({
          path: { 'company-id': companyId, id: conversationId },
        });
      } catch (error) {
        // handleRequestError throws, so the revert has to come first.
        treadAgentStore.clearStopRequest();
        connection.handleRequestError(
          error,
          t('error_messages.tread_agent.failed_to_stop_turn'),
        );
      }
    },
    [treadAgentStore],
  );

  return {
    bulkConfirmToolCalls,
    cancelToolCall,
    confirmToolCall,
    createConversation,
    sendMessage,
    stopTurn,
  };
}
